import React from 'react';
import {
  PhoneForwarded, CheckCircle2, XCircle, AlertTriangle, Zap, Info,
} from 'lucide-react';

/**
 * Chronological timeline of transfer decisions for a single call.
 * Renders `transfer_diagnostics` returned by /api/analytics/transcripts/{id}.
 *
 * Stages emitted by the backend:
 *   - trigger_detected  → why did we decide to transfer?
 *   - attempted         → we picked a live-agent route and spoke the message
 *   - executed          → Telnyx accepted .transfer()
 *   - execution_failed  → Telnyx rejected .transfer() (with error)
 *   - skipped_no_route  → transfer enabled but no live agent available
 *   - skipped_no_config → script complete but transfer not configured
 */

const TRIGGER_LABELS = {
  user_keyword: 'Caller keyword',
  ai_indicator: 'AI phrase',
  ready_flag: 'AI ready flag',
  ai_indicator_realtime: 'AI phrase (real-time)',
  ready_flag_realtime: 'AI ready flag (real-time)',
  intent: 'Intent classifier',
  turn_based: 'Turn-based rule',
  all_info_collected: 'All info collected',
  script_complete: 'Script complete',
  conversation_ended: 'Conversation ended',
};

const STAGE_STYLE = {
  trigger_detected: {
    icon: Zap,
    bg: 'bg-amber-500/10 border-amber-500/40',
    text: 'text-amber-300',
    label: 'Trigger',
  },
  attempted: {
    icon: PhoneForwarded,
    bg: 'bg-blue-500/10 border-blue-500/40',
    text: 'text-blue-300',
    label: 'Attempted',
  },
  executed: {
    icon: CheckCircle2,
    bg: 'bg-emerald-500/10 border-emerald-500/40',
    text: 'text-emerald-300',
    label: 'Executed',
  },
  execution_failed: {
    icon: XCircle,
    bg: 'bg-red-500/10 border-red-500/40',
    text: 'text-red-300',
    label: 'Failed',
  },
  skipped_no_route: {
    icon: AlertTriangle,
    bg: 'bg-orange-500/10 border-orange-500/40',
    text: 'text-orange-300',
    label: 'Skipped',
  },
  skipped_no_config: {
    icon: Info,
    bg: 'bg-gray-500/10 border-gray-500/40',
    text: 'text-gray-400',
    label: 'Skipped',
  },
};

const formatWhen = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

export default function TransferDiagnosticsPanel({ callData }) {
  const rows = callData?.transfer_diagnostics || [];
  const transferTo = callData?.transfer_to;
  const transferUser = callData?.transfer_to_username;
  const scriptCompleted = callData?.script_completed;

  const finalOutcome = (() => {
    for (let i = rows.length - 1; i >= 0; i -= 1) {
      const s = rows[i].stage;
      if (s === 'executed') return { text: 'Transfer executed', style: 'text-emerald-300' };
      if (s === 'execution_failed') return { text: 'Transfer failed', style: 'text-red-300' };
      if (s === 'attempted') return { text: 'Transfer attempted (pending)', style: 'text-blue-300' };
      if (s === 'skipped_no_route') return { text: 'Skipped — no route', style: 'text-orange-300' };
      if (s === 'skipped_no_config') return { text: 'Skipped — not configured', style: 'text-gray-400' };
    }
    return null;
  })();

  return (
    <div className="p-4 border-t border-gray-800" data-testid="transfer-diagnostics-panel">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-400 flex items-center gap-2">
          <PhoneForwarded className="w-4 h-4" />
          Transfer Diagnostics
          {rows.length > 0 && (
            <span className="text-xs text-gray-500">({rows.length} event{rows.length === 1 ? '' : 's'})</span>
          )}
        </h3>
        {finalOutcome && (
          <span className={`text-xs font-medium ${finalOutcome.style}`} data-testid="transfer-final-outcome">
            {finalOutcome.text}
          </span>
        )}
      </div>

      {/* Summary strip */}
      {(transferTo || scriptCompleted) && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs mb-4 pb-4 border-b border-gray-800">
          {transferTo && (
            <div>
              <p className="text-gray-500 mb-0.5">Transfer target</p>
              <p className="text-white font-mono" data-testid="transfer-target-number">{transferTo}</p>
            </div>
          )}
          {transferUser && (
            <div>
              <p className="text-gray-500 mb-0.5">Agent username</p>
              <p className="text-white" data-testid="transfer-target-username">{transferUser}</p>
            </div>
          )}
          {scriptCompleted && (
            <div>
              <p className="text-gray-500 mb-0.5">Script</p>
              <p className="text-emerald-400">Completed</p>
            </div>
          )}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="text-center py-6 border border-dashed border-gray-800 rounded-lg">
          <Info className="w-8 h-8 text-gray-700 mx-auto mb-2" />
          <p className="text-gray-500 text-sm">
            No transfer activity recorded for this call.
          </p>
          <p className="text-gray-600 text-xs mt-1">
            Either the agent has no transfer configured, or the call ended before any trigger fired.
          </p>
        </div>
      ) : (
        <ol className="space-y-2" data-testid="transfer-diagnostics-list">
          {rows.map((row, idx) => {
            const style = STAGE_STYLE[row.stage] || STAGE_STYLE.skipped_no_config;
            const Icon = style.icon;
            const triggerLabel = row.trigger ? (TRIGGER_LABELS[row.trigger] || row.trigger) : null;
            return (
              <li
                key={`${row.stage}-${row.timestamp}-${idx}`}
                className={`flex items-start gap-3 p-3 rounded-lg border ${style.bg}`}
                data-testid={`transfer-diagnostic-row-${idx}`}
              >
                <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${style.text}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-semibold uppercase tracking-wide ${style.text}`}>
                      {style.label}
                    </span>
                    {triggerLabel && (
                      <span className="text-xs px-2 py-0.5 rounded bg-gray-800/60 text-gray-300">
                        {triggerLabel}
                      </span>
                    )}
                    <span className="text-[10px] text-gray-500 font-mono ml-auto">
                      {formatWhen(row.timestamp)}
                    </span>
                  </div>
                  {row.detail && (
                    <p className="text-xs text-gray-300 mt-1 break-words">{row.detail}</p>
                  )}
                  {(row.transfer_number || row.transfer_username) && (
                    <p className="text-[11px] text-gray-500 font-mono mt-1">
                      {row.transfer_number || ''}
                      {row.transfer_username && row.transfer_username !== 'default'
                        ? ` · ${row.transfer_username}`
                        : ''}
                    </p>
                  )}
                  {row.error && (
                    <p className="text-xs text-red-300 mt-1 font-mono break-words">
                      {row.error}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
